import React, { useCallback } from 'react'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Modal from 'LuxKit/Modal/Modal'
import BodyText from 'LuxKit/Typography/BodyText'
import moment from 'moment'
import FormatDateTime from 'components/utils/Formatters/FormatDateTime'
import Group from 'components/utils/Group'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyExperiments } from 'constants/optimizely'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Heading from 'LuxKit/Typography/Heading'
import Step from 'LuxKit/Steppers/Step'
import TextButton from 'LuxKit/Button/TextButton'
import ContactUSP from '../ContactUSP'
import Container from 'components/utils/Sheet'
import { useIntl } from 'react-intl'
interface Props {
  open: boolean
  onClose: () => void
  modalType?: 'bnbl' | 'bestPrice' | 'gifting'
  bookBy: moment.Moment
  travelUntil: moment.Moment
  onViewPackageOptions?: () => void
}

function USPModal(props: Props) {
  const { open, onClose, modalType = '', bookBy, travelUntil, onViewPackageOptions } = props
  const intl = useIntl()

  const isBestPrice = modalType === 'bestPrice'
  const isWePriceMatchEnabled = useOptimizelyExperiment(OptimizelyExperiments.croWePriceMatchOfferPageTag, isBestPrice)

  const modalTitleBnbl = intl.formatMessage({
    id: '792xznAGEQ',
    defaultMessage: 'Buy now, choose dates later',
    description: 'Buy now choose dates later modal title',
  })

  const modalTitleBestPrice = intl.formatMessage({
    id: 'pvDixv6bMk',
    defaultMessage: 'Our best price guarantee',
    description: 'Best price guarantee modal title',
  })

  const modalTitleGifting = intl.formatMessage({
    id: 'wfSu6F+J58',
    defaultMessage: 'Gifting available',
    description: 'Gifting available modal title',
  })

  const modalTitleWePriceMatch = intl.formatMessage({
    id: '7/NeuNyRjj',
    defaultMessage: 'We price match',
    description: 'We price match modal title',
  })

  const modalTitles: Record<string, string> = {
    bnbl: modalTitleBnbl,
    bestPrice: modalTitleBestPrice,
    gifting: modalTitleGifting,
  }

  const title = isBestPrice && isWePriceMatchEnabled ? modalTitleWePriceMatch : modalTitles[modalType]

  const handleViewPackageOptions = useCallback(() => {
    onViewPackageOptions?.()
    onClose()
  }, [onViewPackageOptions, onClose])

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={title}
    >
      {modalType === 'bnbl' && <VerticalSpacer gap={16}>
        <BodyText variant="medium">
          {intl.formatMessage({
            id: 'O3/XMegAJM',
            defaultMessage: 'Our offers are live for a limited time. Secure your dream escape at a great price! No dates yet? Reserve the price now and pick dates later.',
            description: 'Buy now choose dates later intro text',
          })}
        </BodyText>

        <Heading variant="heading5" style={{ marginTop: 24 }}>
          {intl.formatMessage({
            id: '5Kd4c9kjbQ',
            defaultMessage: 'How it works',
            description: 'How it works heading',
          })}
        </Heading>

        <Group direction="vertical" gap={8}>
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <Step number={1} label="" state="inactive" />
            <Group direction="vertical" gap={0}>
              <BodyText variant="large" weight="bold">
                {intl.formatMessage({
                  id: '8a3maVQ6hN',
                  defaultMessage: 'Secure your escape today',
                  description: 'Step 1 title for buy now choose dates later',
                })}
              </BodyText>
              <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'wmnj0lZEln',
                  defaultMessage: 'Purchase now to lock in the price.',
                  description: 'Step 1 description for buy now choose dates later',
                })}
              </BodyText>
            </Group>
          </Group>
          <VerticalSpacer gap={8} />
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <Step number={2} label="" state="inactive" />
            <Group direction="vertical" gap={0}>
              <BodyText variant="large" weight="bold">
                {intl.formatMessage({
                  id: 'NwYlH4cmz2',
                  defaultMessage: 'Choose dates later',
                  description: 'Step 2 title for buy now choose dates later',
                })}
              </BodyText>
              <BodyText variant="medium">
                {intl.formatMessage(
                  {
                    id: '+lFX9AcPI6',
                    defaultMessage: 'Pick dates anytime before <strong>{bookByDate}</strong> in your account.',
                    description: 'Step 2 description for buy now choose dates later',
                  },
                  {
                    bookByDate: moment(bookBy).format(DMY_CASUAL_FORMAT),
                    strong: (chunks: React.ReactNode) => <strong key="strong">{chunks}</strong>,
                  },
                )}
              </BodyText>
            </Group>
          </Group>
          <VerticalSpacer gap={8} />
          <Group direction="horizontal" gap={12} verticalAlign="center">
            <Step number={3} label="" state="inactive" />
            <Group direction="vertical" gap={0}>
              <BodyText variant="large" weight="bold">
                {intl.formatMessage({
                  id: 'QV6Yy1U9lQ',
                  defaultMessage: 'Manage your booking online',
                  description: 'Step 3 title for buy now choose dates later',
                })}
              </BodyText>
              <BodyText variant="medium">
                {intl.formatMessage(
                  {
                    id: 'nevoQrfJAw',
                    defaultMessage: 'Make sure to travel by <strong>{travelUntilDate}</strong>.',
                    description: 'Step 3 description for buy now choose dates later',
                  },
                  {
                    travelUntilDate: moment(travelUntil).format(DMY_CASUAL_FORMAT),
                    strong: (chunks: React.ReactNode) => <strong key="strong">{chunks}</strong>,
                  },
                )}
              </BodyText>
            </Group>
          </Group>
        </Group>

        <MessageBanner kind="informational-primary" density="compact">
          <Group direction="horizontal" gap={8} verticalAlign="center">
            <LineInfoCircleIcon />
            <BodyText variant="medium">
              {intl.formatMessage({
                id: '4RI8/3HFGH',
                defaultMessage: 'Subject to availability and surcharges (peak period or property-specific). Any surcharges will be shown before confirming your dates.',
                description: 'Buy now choose dates later disclaimer text',
              })}
            </BodyText>
          </Group>
        </MessageBanner>

      </VerticalSpacer>}
      {modalType === 'bestPrice' && <VerticalSpacer gap={24}>
        <BodyText variant="medium">
          {intl.formatMessage({
            id: 'RTdl5J15lx',
            defaultMessage: "We negotiate the best prices so you don't have to. In the unlikely event that you find a better price for the same offer, we promise to match it.",
            description: 'Best price guarantee intro text',
          })}
        </BodyText>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            {intl.formatMessage({
              id: 'n7GdxHsRQP',
              defaultMessage: '1. Compare the same offer',
              description: 'Step 1 title for best price guarantee',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: '0nS0sceMCK',
              defaultMessage: "Make sure the offer you've found is for the same hotel with the same VIP inclusions as our package.",
              description: 'Step 1 description for best price guarantee',
            })}
          </BodyText>
          <BodyText weight="bold" variant="medium">
            {intl.formatMessage({
              id: 'yS+YL4cLfx',
              defaultMessage: '2. Record the better price',
              description: 'Step 2 title for best price guarantee',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: '/XAiW1au+Z',
              defaultMessage: 'Take a website screenshot or a photo of a catalogue so you can send it to us.',
              description: 'Step 2 description for best price guarantee',
            })}
          </BodyText>
          <BodyText weight="bold" variant="medium">
            {intl.formatMessage({
              id: 'KRwLJbVDPj',
              defaultMessage: "3. We'll match the price",
              description: 'Step 3 title for best price guarantee',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'cCyX7JSvlz',
              defaultMessage: "Once verified we'll match the price.",
              description: 'Step 3 description for best price guarantee',
            })}
          </BodyText>
        </Group>
      </VerticalSpacer>}
      {modalType === 'gifting' && <VerticalSpacer gap={24}>
        <BodyText variant="medium">
          {intl.formatMessage(
            {
              id: 'gBGn0Hz7iw',
              defaultMessage: 'Buy a package and send as a gift to a special someone. They must book by {bookByDate} and can travel until {travelUntilDate}.',
              description: 'Gifting intro text',
            },
            {
              bookByDate: <FormatDateTime key="bookBy" value={bookBy} format="dmy-casual" />,
              travelUntilDate: <FormatDateTime key="travelUntil" value={travelUntil} format="dmy-casual" />,
            },
          )}
        </BodyText>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            {intl.formatMessage({
              id: 'oFvLzGTyo+',
              defaultMessage: '1. Purchase online',
              description: 'Step 1 title for gifting',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'SdaepxiJQE',
              defaultMessage: 'Purchasing this as a gift will secure the offer for your recipient and give them the flexibility to choose travel dates at a later time. Dates must be within the book by and validity period, and are subject to availability and surcharges.',
              description: 'Step 1 description for gifting',
            })}
          </BodyText>
        </Group>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            {intl.formatMessage({
              id: 'ZYvGeoImyH',
              defaultMessage: '2. Send or schedule your gift',
              description: 'Step 2 title for gifting',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'bgw5a+xXCw',
              defaultMessage: "Provide the recipient's details and schedule a date so we can email it to them on your behalf.",
              description: 'Step 2 description for gifting',
            })}
          </BodyText>
        </Group>
        <Group gap={8} direction="vertical">
          <BodyText weight="bold" variant="medium">
            {intl.formatMessage({
              id: 'ik4kwQ6SqG',
              defaultMessage: '3. The world is their oyster',
              description: 'Step 3 title for gifting',
            })}
          </BodyText>
          <BodyText variant="medium">
            {intl.formatMessage({
              id: 'RCLDHXfJ8w',
              defaultMessage: 'Your recipient can manage and make changes to their booking according to our 7-day ‘change of mind’ guarantee after booking (if applicable – refer to individual offer for more information).',
              description: 'Step 3 description for gifting',
            })}
          </BodyText>
        </Group>
      </VerticalSpacer>}

      <Container style={{ marginTop: 24 }}>
        <ContactUSP title={intl.formatMessage({ defaultMessage: 'Need help?', id: 'krty63gJOp' })} description={intl.formatMessage({ defaultMessage: 'Our team are available 24 hours, 7 days', id: 'Ox7FrGtWYX', description: 'Contact USP description about team availability' })} />
      </Container>

      {
        onViewPackageOptions && (
          <VerticalSpacer gap={24}>
            <Group direction="horizontal" horizontalAlign="end">
              <TextButton
                kind="primary"
                onClick={handleViewPackageOptions}
                fit="mobile-full-width"
                style={{
                  marginTop: 16,
                }}
              >
                {intl.formatMessage({
                  id: 'k5XnlHVDsW',
                  defaultMessage: 'View package options',
                  description: 'View package options button text',
                })}
              </TextButton>
            </Group>
          </VerticalSpacer>
        )
      }

    </Modal>
  )
}

export default USPModal
